import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Camera, Shield, Swords, FlaskConical, TrendingUp, Activity, Send, Loader2, Trophy, Eye, EyeOff, History, GitMerge, X, Award } from "lucide-react";
import { CLASS_COLORS, ENCHANTABLE_SLOTS, WOW_CLASSES } from "@/lib/constants";
import { gearScore, gearGrade, ilvlColor, weakestSlots } from "@/lib/gearScore";
import { getPlayerProgression, updatePlayerRaidData, updatePlayer, sendTabSummary, getPlayerRating, getPlayerBadges, sendPlayerChangelogDiscord, getUpgradeFlags, createUpgradeFlag, deleteUpgradeFlag, mergePlayers } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import GearTab from "@/components/GearTab";
import ConsumablesTab from "@/components/ConsumablesTab";
import PerformanceTab from "@/components/PerformanceTab";
import ChangelogTab from "@/components/ChangelogTab";
import CharacterPanel from "@/components/CharacterPanel";
import MvpVelocityChart from "@/components/MvpVelocityChart";
import { toast } from "sonner";

const WOWHEAD_BASE = "https://www.wowhead.com/tbc";

function DiscordSendButton({ playerId, raidId, tab, label }) {
  const [sending, setSending] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [open, setOpen] = useState(false);

  const handleSend = async () => {
    if (!webhookUrl) { toast.error("Enter a Discord webhook URL"); return; }
    localStorage.setItem("discord_webhook_url", webhookUrl);
    setSending(true);
    try {
      await sendTabSummary(webhookUrl, playerId, raidId, tab);
      toast.success(`${label} summary sent to Discord!`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`discord-send-${tab}`} variant="outline" size="sm"
          className="border-[#5865F2]/40 text-[#5865F2] hover:bg-[#5865F2]/10 rounded-sm text-xs gap-1">
          <Send className="h-3 w-3" /> Send to Discord
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm bg-background">
        <DialogHeader>
          <DialogTitle className="heading text-base">Send {label} to Discord</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
            className="bg-card/50 text-xs" />
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
            </DialogClose>
            <Button size="sm" disabled={!webhookUrl || sending} onClick={handleSend}
              className="rounded-sm text-xs bg-[#5865F2] text-white hover:bg-[#4752C4]">
              {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChangelogDiscordButton({ playerId }) {
  const [sending, setSending] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [open, setOpen] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const getWeek = (offset) => {
    const now = new Date();
    now.setDate(now.getDate() + offset * 7);
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      start: monday.toISOString().split("T")[0],
      end: sunday.toISOString().split("T")[0],
      label: `${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${sunday.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    };
  };

  const week = getWeek(weekOffset);

  const handleSend = async () => {
    if (!webhookUrl) { toast.error("Enter a Discord webhook URL"); return; }
    localStorage.setItem("discord_webhook_url", webhookUrl);
    setSending(true);
    try {
      await sendPlayerChangelogDiscord(webhookUrl, playerId, week.start, week.end);
      toast.success("Changelog sent to Discord!");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="discord-send-changelog" variant="outline" size="sm"
          className="border-[#5865F2]/40 text-[#5865F2] hover:bg-[#5865F2]/10 rounded-sm text-xs gap-1">
          <Send className="h-3 w-3" /> Send to Discord
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm bg-background">
        <DialogHeader>
          <DialogTitle className="heading text-base">Send Changelog to Discord</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1 block">Week</label>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setWeekOffset(weekOffset - 1)}>
                <span className="text-xs">&lt;</span>
              </Button>
              <span className="text-xs font-mono flex-1 text-center" data-testid="changelog-week-label">{week.label}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setWeekOffset(weekOffset + 1)}>
                <span className="text-xs">&gt;</span>
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-0.5 font-mono">{week.start} to {week.end}</p>
          </div>
          <Input value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/..."
            className="bg-card/50 text-xs" />
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
            </DialogClose>
            <Button size="sm" disabled={!webhookUrl || sending} onClick={handleSend}
              className="rounded-sm text-xs bg-[#5865F2] text-white hover:bg-[#4752C4]">
              {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
              {sending ? "Sending..." : "Send Week"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function PlayerDetail({ playerId, raids, team, allPlayers = [], onDataChanged }) {
  const { isAdmin } = useAuth() || {};
  const [player, setPlayer] = useState(null);
  const [progression, setProgression] = useState([]);
  const [selectedRaid, setSelectedRaid] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [rating, setRating] = useState(null);
  const [badges, setBadges] = useState([]);
  const [mvpHistory, setMvpHistory] = useState([]);
  const [upgradeFlags, setUpgradeFlags] = useState([]);
  const [hiddenBadges, setHiddenBadges] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hidden_badges") || "[]"); } catch { return []; }
  });

  const loadProgression = useCallback(async () => {
    if (!playerId) return;
    setLoading(true);
    try {
      const [res, rat, badgeData] = await Promise.all([
        getPlayerProgression(playerId),
        getPlayerRating(playerId).catch(() => null),
        getPlayerBadges(playerId).catch(() => ({ badges: [], mvp_history: [] })),
      ]);
      setPlayer(res.player);
      setProgression(res.progression);
      setRating(rat);
      setBadges(badgeData.badges || []);
      setMvpHistory(badgeData.mvp_history || []);
      getUpgradeFlags(playerId).then(setUpgradeFlags).catch(() => {});
      if (res.progression.length > 0) {
        const latest = res.progression[res.progression.length - 1];
        setSelectedRaid(latest.raid_id);
        setCurrentData(latest);
      }
    } catch (e) {
      console.error("Failed to load player:", e);
    }
    setLoading(false);
  }, [playerId]);

  useEffect(() => { loadProgression(); }, [loadProgression]);

  useEffect(() => {
    if (selectedRaid && progression.length > 0) {
      const data = progression.find((p) => p.raid_id === selectedRaid);
      setCurrentData(data || null);
    }
  }, [selectedRaid, progression]);

  const handleClassChange = async (newClass) => {
    try {
      await updatePlayer(playerId, { wow_class: newClass });
      setPlayer((prev) => ({ ...prev, wow_class: newClass }));
      onDataChanged();
      toast.success(`Class updated to ${newClass} (synced across teams)`);
    } catch (e) {
      toast.error("Failed to update class");
    }
  };

  const handleSaveData = async (field, value) => {
    if (!selectedRaid || !currentData) return;
    try {
      const updated = await updatePlayerRaidData(playerId, selectedRaid, { [field]: value });
      setCurrentData(updated);
      setProgression((prev) =>
        prev.map((p) => (p.raid_id === selectedRaid ? { ...p, ...updated } : p))
      );
      toast.success("Data saved");
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const handleFlag = async (slot, flagType, flaggedId, flaggedName) => {
    try {
      const flag = await createUpgradeFlag({
        player_id: playerId,
        raid_id: selectedRaid,
        slot,
        flag_type: flagType,
        flagged_id: flaggedId,
        flagged_name: flaggedName,
      });
      setUpgradeFlags((prev) => [...prev, flag]);
      toast.success(`Flagged ${flagType} on ${slot} for upgrade`);
    } catch (e) {
      toast.error("Failed to flag");
    }
  };

  const handleUnflag = async (flagId) => {
    try {
      await deleteUpgradeFlag(flagId);
      setUpgradeFlags((prev) => prev.filter((f) => f.id !== flagId));
      toast.success("Flag removed");
    } catch (e) {
      toast.error("Failed to remove flag");
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="player-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-card/50 rounded w-48" />
          <div className="h-4 bg-card/50 rounded w-32" />
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="p-6 md:p-8 lg:p-12" data-testid="player-not-found">
        <p className="text-muted-foreground">Player not found.</p>
      </div>
    );
  }

  const classColor = CLASS_COLORS[player.wow_class] || CLASS_COLORS.Unknown;

  return (
    <div className={`p-6 md:p-8 lg:p-12 animate-fade-in ${screenshotMode ? "screenshot-mode" : ""}`}
         data-testid="player-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight"
            style={{ color: classColor }} data-testid="player-name-heading">
            {player.name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <Select value={player.wow_class} onValueChange={handleClassChange}>
              <SelectTrigger data-testid="class-selector"
                className="h-7 text-xs w-auto border-0 bg-transparent px-0 gap-1" style={{ color: classColor }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WOW_CLASSES.map((c) => (
                  <SelectItem key={c} value={c}>
                    <span style={{ color: CLASS_COLORS[c] }}>{c}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="text-xs font-mono">{team}</Badge>
            <Badge variant="outline" className="text-xs font-mono">{progression.length} raid(s)</Badge>
            {/* Talent spec badge */}
            {currentData?.spec?.spec_name && currentData.spec.spec_name !== "Unknown" && (
              <Badge
                data-testid="spec-badge"
                className="text-xs font-mono gap-1.5 bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25"
                title={currentData.spec.build ? `${currentData.spec.build} talent build` : `${currentData.spec.spec_name} spec`}
              >
                <span className="font-bold">{currentData.spec.spec_short || currentData.spec.spec_name}</span>
                {currentData.spec.build && (
                  <>
                    <span className="opacity-60">·</span>
                    <span className="opacity-80">{currentData.spec.build}</span>
                  </>
                )}
                {currentData.spec.is_hybrid && (
                  <span className="text-[9px] uppercase tracking-wider opacity-70 ml-1">hybrid</span>
                )}
              </Badge>
            )}
            {/* Gear Score grade */}
            {(() => {
              const score = gearScore(currentData?.gear);
              if (!score) return null;
              const grade = gearGrade(score);
              return (
                <Badge
                  data-testid="gear-score-badge"
                  className="text-xs font-mono gap-1.5 border bg-card/60"
                  style={{ borderColor: `${grade.color}66`, color: grade.color }}
                  title={`Gear score ${score} — ${grade.label}`}
                >
                  <Award className="h-3 w-3" />
                  <span className="font-bold">{grade.letter}</span>
                  <span className="opacity-70">·</span>
                  <span className="opacity-90">iLvl {score}</span>
                </Badge>
              );
            })()}
            {Array.isArray(player.aliases) && player.aliases.length > 0 && (
              <span className="text-xs text-muted-foreground font-mono" data-testid="player-aliases">
                aka {player.aliases.join(", ")}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && player && (
            <MergeDialog
              player={player}
              allPlayers={allPlayers}
              onMerged={(newPlayerName) => {
                toast.success(`Merged into ${newPlayerName}`);
                onDataChanged();
              }}
            />
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button data-testid="screenshot-mode-btn" variant="ghost" size="icon"
                onClick={() => setScreenshotMode(!screenshotMode)} className={screenshotMode ? "bg-accent" : ""}>
                <Camera className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Screenshot Mode</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Player Rating Card */}
      {rating && rating.grade !== "N/A" && (
        <div className="mb-6" data-testid="player-rating-card">
          <div className="grid grid-cols-[auto_1fr] gap-4 items-center stat-card">
            <div className="flex flex-col items-center justify-center w-16 h-16">
              <span className={`text-3xl font-black font-mono leading-none ${
                rating.grade === "S" ? "text-orange-400" :
                rating.grade === "A" ? "text-purple-400" :
                rating.grade === "B" ? "text-blue-400" :
                rating.grade === "C" ? "text-green-400" :
                rating.grade === "D" ? "text-yellow-500" : "text-destructive"
              }`} data-testid="player-grade">{rating.grade}</span>
              <span className="text-xs font-mono text-muted-foreground">{rating.overall}/100</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RatingBar label="Gear" value={rating.gear} />
              <RatingBar label="Consumables" value={rating.consumables} />
              <RatingBar label="Performance" value={rating.performance} />
              <RatingBar label="Attendance" value={rating.attendance} extra={`${rating.raids_attended}/${rating.total_raids}`} />
            </div>
          </div>
        </div>
      )}

      {/* Weakest 3 slots callout */}
      {(() => {
        const weak = weakestSlots(currentData?.gear, 3);
        if (weak.length === 0) return null;
        return (
          <div className="stat-card p-4 mb-6" data-testid="weakest-slots">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="heading text-sm font-bold uppercase tracking-tight text-amber-400">
                ⚠️ Weakest slots ({weak.length})
              </h3>
              <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                Compared to this player's mean ilvl
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {weak.map((g) => {
                const c = ilvlColor(g.item_level);
                return (
                  <div key={g.slot} className="flex items-center justify-between px-3 py-2 rounded-sm bg-card/40 border border-border/30"
                       data-testid={`weak-slot-${g.slot.toLowerCase().replace(/\s/g, "-")}`}>
                    <div>
                      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{g.slot}</div>
                      <a href={`https://www.wowhead.com/tbc/item=${g.item_id}`} target="_blank" rel="noreferrer"
                         className="text-sm font-medium hover:underline" style={{ color: c.hex }}>
                        iLvl {g.item_level} · {c.label}
                      </a>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-amber-400 font-bold">-{g.gap}</div>
                      <div className="text-[9px] text-muted-foreground uppercase">behind</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Badges & MVP History */}
      {(badges.length > 0 || mvpHistory.length > 0) && (
        <PlayerBadgesSection
          badges={badges}
          mvpHistory={mvpHistory}
          hiddenBadges={hiddenBadges}
          screenshotMode={screenshotMode}
          onToggleHidden={(badgeId) => {
            setHiddenBadges((prev) => {
              const next = prev.includes(badgeId) ? prev.filter((b) => b !== badgeId) : [...prev, badgeId];
              localStorage.setItem("hidden_badges", JSON.stringify(next));
              return next;
            });
          }}
        />
      )}

      {/* MVP Velocity Chart */}
      {progression.length > 1 && (
        <MvpVelocityChart playerId={playerId} />
      )}

      {/* Raid Selector */}
      {progression.length > 0 && (
        <div className="mb-6">
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Raid Log</label>
          <Select value={selectedRaid || ""} onValueChange={setSelectedRaid}>
            <SelectTrigger data-testid="raid-selector" className="bg-card/50 max-w-md">
              <SelectValue placeholder="Select a raid" />
            </SelectTrigger>
            <SelectContent>
              {progression.map((p) => (
                <SelectItem key={p.raid_id} value={p.raid_id}>
                  {p.raid_info?.raid_name || "Unknown"} — {p.raid_info?.date || ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {progression.length === 0 ? (
        <div className="stat-card text-center py-12">
          <p className="text-muted-foreground">No raid data for this player yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Import an RPB file to add data.</p>
        </div>
      ) : currentData ? (
        <Tabs defaultValue="gear" className="w-full">
          <TabsList className="bg-card/50 p-1 h-auto flex-wrap" data-testid="player-tabs">
            <TabsTrigger data-testid="tab-character" value="character" className="gap-1.5 text-xs">
              <Shield className="h-3.5 w-3.5" /> Character
            </TabsTrigger>
            <TabsTrigger data-testid="tab-gear" value="gear" className="gap-1.5 text-xs">
              <Shield className="h-3.5 w-3.5" /> Gear
            </TabsTrigger>
            <TabsTrigger data-testid="tab-consumables" value="consumables" className="gap-1.5 text-xs">
              <FlaskConical className="h-3.5 w-3.5" /> Consumables
            </TabsTrigger>
            <TabsTrigger data-testid="tab-performance" value="performance" className="gap-1.5 text-xs">
              <TrendingUp className="h-3.5 w-3.5" /> Performance
            </TabsTrigger>
            <TabsTrigger data-testid="tab-combat" value="combat" className="gap-1.5 text-xs">
              <Activity className="h-3.5 w-3.5" /> Combat
            </TabsTrigger>
            <TabsTrigger data-testid="tab-changelog" value="changelog" className="gap-1.5 text-xs">
              <History className="h-3.5 w-3.5" /> Changelog
            </TabsTrigger>
          </TabsList>

          <TabsContent value="character">
            <CharacterPanel data={currentData} wowClass={player?.wow_class} />
          </TabsContent>

          <TabsContent value="gear">
            <div className="flex justify-end mb-2">
              <DiscordSendButton playerId={playerId} raidId={selectedRaid} tab="gear" label="Gear" />
            </div>
            <GearTab data={currentData}
              upgradeFlags={upgradeFlags} onFlag={handleFlag} onUnflag={handleUnflag} />
          </TabsContent>

          <TabsContent value="consumables">
            <div className="flex justify-end mb-2">
              <DiscordSendButton playerId={playerId} raidId={selectedRaid} tab="consumables" label="Consumables" />
            </div>
            <ConsumablesTab data={currentData} />
          </TabsContent>

          <TabsContent value="performance">
            <div className="flex justify-end mb-2">
              <DiscordSendButton playerId={playerId} raidId={selectedRaid} tab="performance" label="Performance" />
            </div>
            <PerformanceTab data={currentData} progression={progression} allPlayers={allPlayers} onSave={(field, value) => handleSaveData(field, value)} />
          </TabsContent>

          <TabsContent value="combat">
            <div className="flex justify-end mb-2">
              <DiscordSendButton playerId={playerId} raidId={selectedRaid} tab="combat" label="Combat" />
            </div>
            <CombatTab data={currentData} />
          </TabsContent>

          <TabsContent value="changelog">
            <div className="flex justify-end mb-2">
              <ChangelogDiscordButton playerId={playerId} />
            </div>
            <ChangelogTab playerId={playerId} />
          </TabsContent>
        </Tabs>
      ) : null}
    </div>
  );
}

function refreshWowheadTooltips() {
  try {
    if (window.$WowheadPower && window.$WowheadPower.refreshLinks) {
      window.$WowheadPower.refreshLinks();
    }
  } catch (e) {
    // Wowhead tooltip refresh is best-effort; silently ignore if script isn't loaded yet
    if (process.env.NODE_ENV === "development") console.debug("Wowhead refreshLinks skipped:", e);
  }
}

function CombatTab({ data }) {
  const cs = data?.combat_stats || {};
  const stats = [
    { label: "Deaths", value: cs.deaths ?? 0, bad: (cs.deaths || 0) > 0 },
    { label: "Avoidable Damage", value: cs.avoidable_damage ?? 0, bad: (cs.avoidable_damage || 0) > 10000 },
    { label: "Avoidable Debuffs", value: cs.avoidable_debuffs_pct != null ? `${cs.avoidable_debuffs_pct}%` : "—" },
    { label: "Friendly Fire", value: cs.friendly_fire ?? 0, bad: (cs.friendly_fire || 0) > 10000 },
  ];
  const rawStats = data?.raw_stats || {};
  const trinkets = data?.trinkets || [];
  const absorbed = data?.damage_absorbed || {};
  const engineering = data?.engineering || {};
  const interruptCount = data?.interrupted_spells || 0;
  const interruptDetails = data?.interrupt_details || [];

  useEffect(() => {
    setTimeout(refreshWowheadTooltips, 500);
  }, [interruptDetails]);

  return (
    <div className="space-y-6 mt-4" data-testid="combat-tab">
      <div>
        <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Combat Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="stat-card">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <p className={`text-lg font-bold font-mono ${s.bad ? "text-destructive" : ""}`}>
                {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Interrupt Details */}
      <div>
        <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">
          Interrupts ({interruptCount})
        </h3>
        {interruptDetails.length > 0 ? (
          <div className="space-y-1">
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 px-3 py-1.5">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Interrupted Spell</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Target</span>
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Used Spell</span>
            </div>
            {interruptDetails.map((d, i) => (
              <div key={i} data-testid={`interrupt-detail-${i}`}
                className="grid grid-cols-[1fr_1fr_1fr] gap-3 px-3 py-2 items-center hover:bg-card/50 border-b border-border/20">
                {d.interrupted_spell_id > 0 ? (
                  <a href={`${WOWHEAD_BASE}/spell=${d.interrupted_spell_id}`}
                    onClick={(e) => e.preventDefault()}
                    className="text-sm text-red-400 hover:underline">
                    {d.interrupted_spell}
                  </a>
                ) : (
                  <span className="text-sm">{d.interrupted_spell || "?"}</span>
                )}
                <span className="text-sm text-muted-foreground">{d.target || "?"}</span>
                {d.interrupt_spell_id > 0 ? (
                  <a href={`${WOWHEAD_BASE}/spell=${d.interrupt_spell_id}`}
                    onClick={(e) => e.preventDefault()}
                    className="text-sm text-blue-400 hover:underline">
                    {d.interrupt_spell}
                  </a>
                ) : (
                  <span className="text-sm">{d.interrupt_spell || "?"}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="font-mono text-sm text-muted-foreground">
            {interruptCount > 0 ? `${interruptCount} interrupt(s) — re-import combat log for details` : "No interrupts recorded"}
          </p>
        )}
      </div>

      {trinkets.length > 0 && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Trinkets</h3>
          <div className="flex flex-wrap gap-2">
            {trinkets.map((t, i) => (
              <Badge key={i} variant="outline" className="font-mono text-xs">{t}</Badge>
            ))}
          </div>
        </div>
      )}

      {Object.keys(absorbed).length > 0 && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Damage Absorbed</h3>
          <table className="data-table w-full">
            <thead><tr><th>Source</th><th>Amount</th></tr></thead>
            <tbody>
              {Object.entries(absorbed).map(([k, v]) => (
                <tr key={k}><td>{k}</td><td className="font-mono">{typeof v === "number" ? v.toLocaleString() : v}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Object.keys(engineering).length > 0 && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Engineering</h3>
          <table className="data-table w-full">
            <thead><tr><th>Item</th><th>Value</th></tr></thead>
            <tbody>
              {Object.entries(engineering).map(([k, v]) => (
                <tr key={k}><td>{k}</td><td className="font-mono">{typeof v === "number" ? v.toLocaleString() : v}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Object.keys(rawStats).length > 0 && (
        <div>
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Raw Stats</h3>
          <table className="data-table w-full">
            <thead><tr><th>Stat</th><th>Value</th></tr></thead>
            <tbody>
              {Object.entries(rawStats).map(([k, v]) => (
                <tr key={k}><td>{k}</td><td className="font-mono">{typeof v === "number" ? v.toLocaleString() : String(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function RatingBar({ label, value, extra }) {
  const color = value >= 80 ? "bg-green-500" : value >= 60 ? "bg-blue-500" : value >= 40 ? "bg-yellow-500" : "bg-destructive";
  const textColor = value >= 80 ? "text-green-500" : value >= 60 ? "text-blue-500" : value >= 40 ? "text-yellow-500" : "text-destructive";
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`text-xs font-mono font-bold ${textColor}`}>{Math.round(value)}%</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${value}%` }} />
      </div>
      {extra && <span className="text-[10px] font-mono text-muted-foreground">{extra}</span>}
    </div>
  );
}



const RANK_LABELS = { 1: "🥇", 2: "🥈", 3: "🥉" };

function PlayerBadgesSection({ badges, mvpHistory, hiddenBadges, screenshotMode, onToggleHidden }) {
  const visibleBadges = screenshotMode
    ? badges.filter((b) => !hiddenBadges.includes(b.id))
    : badges;

  const positiveBadges = visibleBadges.filter((b) => b.type === "positive");
  const negativeBadges = visibleBadges.filter((b) => b.type === "negative");
  const hasContent = visibleBadges.length > 0 || mvpHistory.length > 0;

  if (!hasContent) return null;

  return (
    <div className="mb-6" data-testid="player-badges-section">
      {/* MVP History */}
      {mvpHistory.length > 0 && (
        <div className="mb-4" data-testid="mvp-history">
          <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Trophy className="h-3.5 w-3.5 text-yellow-500" /> MVP Awards
          </h3>
          <div className="flex flex-wrap gap-2">
            {mvpHistory.map((m, i) => (
              <div key={i} data-testid={`mvp-award-${m.date}`}
                className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-yellow-500/10 border border-yellow-500/20">
                <span className="text-base">{RANK_LABELS[m.rank] || `#${m.rank}`}</span>
                <div>
                  <p className="text-xs font-medium text-yellow-400">{m.raid_name}</p>
                  <p className="text-[10px] text-muted-foreground">{m.date} &middot; Score {m.score}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pattern Badges */}
      {visibleBadges.length > 0 && (
        <div data-testid="pattern-badges">
          <h3 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
            Pattern Badges
          </h3>
          <div className="flex flex-wrap gap-2">
            {positiveBadges.map((b) => (
              <BadgeChip key={b.id} badge={b} screenshotMode={screenshotMode}
                hidden={hiddenBadges.includes(b.id)} onToggle={() => onToggleHidden(b.id)} />
            ))}
            {negativeBadges.map((b) => (
              <BadgeChip key={b.id} badge={b} screenshotMode={screenshotMode}
                hidden={hiddenBadges.includes(b.id)} onToggle={() => onToggleHidden(b.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Hidden badges indicator (edit mode only) */}
      {!screenshotMode && hiddenBadges.length > 0 && badges.some((b) => hiddenBadges.includes(b.id)) && (
        <p className="text-[10px] text-muted-foreground mt-2">
          <EyeOff className="h-3 w-3 inline mr-1" />
          {badges.filter((b) => hiddenBadges.includes(b.id)).length} badge(s) hidden in screenshot mode
        </p>
      )}
    </div>
  );
}

function BadgeChip({ badge, screenshotMode, hidden, onToggle }) {
  const isPositive = badge.type === "positive";
  const bgColor = isPositive ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20";
  const textColor = isPositive ? "text-emerald-400" : "text-red-400";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div data-testid={`badge-${badge.id}`}
          className={`group relative flex items-center gap-1.5 px-3 py-1.5 rounded-sm border transition-all ${bgColor} ${hidden ? "opacity-40" : ""}`}>
          <span className="text-base">{badge.emoji}</span>
          <span className={`text-xs font-medium ${textColor}`}>{badge.name}</span>
          {!screenshotMode && (
            <button onClick={onToggle} data-testid={`toggle-badge-${badge.id}`}
              className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {hidden ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
            </button>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{badge.description}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function MergeDialog({ player, allPlayers, onMerged }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [busy, setBusy] = useState(false);

  // Candidate sources: any OTHER player on the same team
  const candidates = (allPlayers || [])
    .filter((p) => p.id !== player.id && (p.team || "") === (player.team || ""))
    .filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 50);

  const selectedSource = candidates.find((c) => c.id === sourceId) || allPlayers.find((p) => p.id === sourceId);

  const handleMerge = async () => {
    if (!sourceId) {
      toast.error("Pick the old character to merge in");
      return;
    }
    if (!window.confirm(
      `Merge "${selectedSource?.name}" into "${player.name}"? All raid data, flags, and notes will be moved over. "${selectedSource?.name}" will be deleted.`
    )) return;
    setBusy(true);
    try {
      const res = await mergePlayers(player.id, sourceId);
      onMerged(res.target?.name || player.name);
      setOpen(false);
      setSourceId("");
      setSearch("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="merge-player-btn" variant="ghost" size="icon" title="Merge an old name into this character">
          <GitMerge className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-background">
        <DialogHeader>
          <DialogTitle className="heading text-base">
            Merge into <span style={{ color: CLASS_COLORS[player.wow_class] || "#fff" }}>{player.name}</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Pick the previous character to fold into <strong>{player.name}</strong>. All raid data, gear changes, upgrade flags and notes will be re-pointed at this character. The old name is kept as an alias.
        </p>
        <Input
          data-testid="merge-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search players on this team..."
          className="bg-card/50 text-xs"
        />
        <div className="max-h-[300px] overflow-y-auto space-y-1 -mx-1 px-1" data-testid="merge-candidates">
          {candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No matching players</p>
          ) : (
            candidates.map((c) => (
              <button
                key={c.id}
                data-testid={`merge-candidate-${c.id}`}
                onClick={() => setSourceId(c.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-sm text-left transition-colors ${
                  sourceId === c.id ? "bg-accent" : "hover:bg-card/60"
                }`}
              >
                <span className="text-sm font-mono" style={{ color: CLASS_COLORS[c.wow_class] || "#fff" }}>
                  {c.name}
                </span>
                <span className="text-[10px] text-muted-foreground">{c.wow_class}</span>
              </button>
            ))
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <DialogClose asChild>
            <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
          </DialogClose>
          <Button
            data-testid="confirm-merge-btn"
            onClick={handleMerge}
            disabled={busy || !sourceId}
            size="sm"
            className="text-xs rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <GitMerge className="h-3 w-3 mr-1" />}
            Merge
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
